import { useRef, useCallback } from 'react';
import {
  Table,
  Button,
  Tag,
  Typography,
  Space,
  Spin,
  Progress,
  InputNumber,
  Popconfirm,
  Descriptions,
  message,
} from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useInventoryCountById,
  useStartCount,
  useRecordCount,
  useCompleteCount,
} from '@hooks/useInventory';
import { downloadInventoryCountPdf } from '@/api/exportApi';
import type {
  InventoryCountLineDto,
  InventoryCountStatus,
} from '@/types/inventory';

const MD_STATUS_COLOR_MAP: Record<InventoryCountStatus, string> = {
  Draft: 'default',
  InProgress: 'processing',
  Completed: 'success',
  Cancelled: 'error',
};

const MD_STATUS_LABEL_MAP: Record<InventoryCountStatus, string> = {
  Draft: 'Чернетка',
  InProgress: 'В процесі',
  Completed: 'Завершено',
  Cancelled: 'Скасовано',
};

interface InventoryCountDetailPageProps {
  countId: string;
  onBack: () => void;
}

export default function InventoryCountDetailPage({
  countId,
  onBack,
}: InventoryCountDetailPageProps) {
  const mdInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const { data: mdCount, isLoading: mdIsLoading } = useInventoryCountById(countId);
  const mdStartCount = useStartCount();
  const mdRecordCount = useRecordCount(countId);
  const mdCompleteCount = useCompleteCount();

  const mdHandleStart = () => {
    mdStartCount.mutate(countId);
  };

  const mdHandleComplete = () => {
    mdCompleteCount.mutate(countId, {
      onSuccess: () => {
        message.success('Інвентаризацію завершено та коригування застосовано');
      },
    });
  };

  const mdHandleRecordQuantity = useCallback(
    (mdLineId: string, mdQuantity: number | null) => {
      if (mdQuantity === null || mdQuantity === undefined) return;
      mdRecordCount.mutate(
        { lineId: mdLineId, countedQuantity: mdQuantity },
        {
          onSuccess: () => {
            // Focus next uncounted line
            if (!mdCount?.lines) return;
            const mdCurrentIndex = mdCount.lines.findIndex(
              (mdLine) => mdLine.id === mdLineId,
            );
            const mdNextUncounted = mdCount.lines.find(
              (mdLine, mdIdx) =>
                mdIdx > mdCurrentIndex && mdLine.countedQuantity === null,
            );
            if (mdNextUncounted) {
              const mdNextInput = mdInputRefs.current.get(mdNextUncounted.id);
              mdNextInput?.focus();
            }
          },
        },
      );
    },
    [mdRecordCount, mdCount?.lines],
  );

  const mdHandlePrint = async () => {
    await downloadInventoryCountPdf(countId);
  };

  const mdGetDiscrepancyColor = (mdDiscrepancy: number | null): string => {
    if (mdDiscrepancy === null) return '';
    if (mdDiscrepancy === 0) return '#52c41a';
    if (mdDiscrepancy < 0) return '#ff4d4f';
    return '#fa8c16';
  };

  const mdIsInProgress = mdCount?.status === 'InProgress';
  const mdIsDraft = mdCount?.status === 'Draft';
  const mdIsCompleted = mdCount?.status === 'Completed';

  const mdCountedPercent =
    mdCount && mdCount.totalLines > 0
      ? Math.round((mdCount.countedLines / mdCount.totalLines) * 100)
      : 0;

  const mdLineColumns: ColumnsType<InventoryCountLineDto> = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
    },
    {
      title: 'Назва',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Партія',
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      width: 140,
    },
    {
      title: 'Системна к-ть',
      dataIndex: 'systemQuantity',
      key: 'systemQuantity',
      width: 130,
      align: 'right',
    },
    {
      title: 'Фактична к-ть',
      key: 'countedQuantity',
      width: 150,
      align: 'right',
      render: (_: unknown, mdRecord: InventoryCountLineDto) => {
        if (!mdIsInProgress) {
          return mdRecord.countedQuantity ?? '—';
        }
        return (
          <InputNumber
            ref={(mdRef) => {
              if (mdRef) {
                const mdInput = (mdRef as unknown as { nativeElement?: HTMLInputElement }).nativeElement;
                if (mdInput) {
                  mdInputRefs.current.set(mdRecord.id, mdInput);
                }
              }
            }}
            min={0}
            precision={0}
            defaultValue={mdRecord.countedQuantity ?? undefined}
            onPressEnter={(mdEvent) => {
              const mdTarget = mdEvent.target as HTMLInputElement;
              const mdValue = mdTarget.value ? Number(mdTarget.value) : null;
              mdHandleRecordQuantity(mdRecord.id, mdValue);
            }}
            onBlur={(mdEvent) => {
              const mdValue = mdEvent.target.value ? Number(mdEvent.target.value) : null;
              if (mdValue !== null && mdValue !== mdRecord.countedQuantity) {
                mdHandleRecordQuantity(mdRecord.id, mdValue);
              }
            }}
            style={{ width: '100%' }}
            size="small"
          />
        );
      },
    },
    {
      title: 'Розбіжність',
      key: 'discrepancy',
      width: 120,
      align: 'right',
      render: (_: unknown, mdRecord: InventoryCountLineDto) => {
        if (mdRecord.discrepancy === undefined || mdRecord.discrepancy === null) {
          return '—';
        }
        const mdColor = mdGetDiscrepancyColor(mdRecord.discrepancy);
        const mdPrefix = mdRecord.discrepancy > 0 ? '+' : '';
        return (
          <span style={{ color: mdColor, fontWeight: 600 }}>
            {mdPrefix}{mdRecord.discrepancy}
          </span>
        );
      },
    },
    {
      title: 'Застосовано',
      dataIndex: 'applied',
      key: 'applied',
      width: 110,
      align: 'center',
      render: (mdValue: boolean) =>
        mdValue ? <Tag color="green">Так</Tag> : <Tag>Ні</Tag>,
    },
  ];

  if (mdIsLoading || !mdCount) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Назад
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Інвентаризація {mdCount.number}
        </Typography.Title>
        <Tag color={MD_STATUS_COLOR_MAP[mdCount.status] ?? 'default'}>
          {MD_STATUS_LABEL_MAP[mdCount.status] ?? mdCount.status}
        </Tag>
      </Space>

      <Descriptions bordered column={3} size="small" style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Локація">
          {mdCount.locationCode ?? 'Вся'}
        </Descriptions.Item>
        <Descriptions.Item label="Розпочато">
          {mdCount.startedAt
            ? dayjs(mdCount.startedAt).format('DD.MM.YYYY HH:mm')
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Завершено">
          {mdCount.completedAt
            ? dayjs(mdCount.completedAt).format('DD.MM.YYYY HH:mm')
            : '—'}
        </Descriptions.Item>
        {mdCount.notes && (
          <Descriptions.Item label="Примітки" span={3}>
            {mdCount.notes}
          </Descriptions.Item>
        )}
      </Descriptions>

      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong>
          Прогрес: {mdCount.countedLines} / {mdCount.totalLines} рядків
        </Typography.Text>
        <Progress
          percent={mdCountedPercent}
          status={mdIsCompleted ? 'success' : 'active'}
          style={{ maxWidth: 400 }}
        />
      </div>

      <Table<InventoryCountLineDto>
        columns={mdLineColumns}
        dataSource={mdCount.lines}
        rowKey="id"
        pagination={false}
        size="small"
      />

      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          borderTop: '1px solid #f0f0f0',
          marginTop: 16,
        }}
      >
        <Button
          icon={<PrinterOutlined />}
          onClick={mdHandlePrint}
        >
          Друк
        </Button>
        {mdIsDraft && (
          <Button
            type="primary"
            onClick={mdHandleStart}
            loading={mdStartCount.isPending}
          >
            Розпочати
          </Button>
        )}
        {mdIsInProgress && (
          <Popconfirm
            title="Завершити інвентаризацію?"
            description="Розбіжності будуть застосовані як коригування залишків. Цю дію неможливо скасувати."
            onConfirm={mdHandleComplete}
            okText="Так, завершити"
            cancelText="Скасувати"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="primary"
              loading={mdCompleteCount.isPending}
            >
              Завершити та застосувати
            </Button>
          </Popconfirm>
        )}
      </div>
    </>
  );
}
